import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import api from '../../services/api'

// ===== Thunks: catálogos =====
export const fetchCatalogos = createAsyncThunk('claves/fetchCatalogos', async (_, { rejectWithValue }) => {
  try {
    return (await api.get('/claves/catalogos')).data.data
  } catch (e) {
    return rejectWithValue(e.response?.data?.message || 'Error al cargar catálogos de claves')
  }
})

export const createCatalogo = createAsyncThunk('claves/createCatalogo', async (data, { rejectWithValue }) => {
  try { return (await api.post('/claves/catalogos', data)).data.data }
  catch (e) { return rejectWithValue(e.response?.data?.message || 'Error al crear catálogo') }
})

export const updateCatalogo = createAsyncThunk('claves/updateCatalogo', async ({ id, data }, { rejectWithValue }) => {
  try { return (await api.put(`/claves/catalogos/${id}`, data)).data.data }
  catch (e) { return rejectWithValue(e.response?.data?.message || 'Error al actualizar catálogo') }
})

export const activarCatalogo = createAsyncThunk('claves/activarCatalogo', async (id, { rejectWithValue }) => {
  try { return (await api.put(`/claves/catalogos/${id}/activar`)).data.data }
  catch (e) { return rejectWithValue(e.response?.data?.message || 'Error al activar catálogo') }
})

export const deleteCatalogo = createAsyncThunk('claves/deleteCatalogo', async (id, { rejectWithValue }) => {
  try { await api.delete(`/claves/catalogos/${id}`); return id }
  catch (e) { return rejectWithValue(e.response?.data?.message || 'Error al eliminar catálogo') }
})

// ===== Thunks: claves =====
export const fetchClavesDeCatalogo = createAsyncThunk('claves/fetchClavesDeCatalogo', async (catalogoId, { rejectWithValue }) => {
  try {
    const r = await api.get(`/claves/catalogos/${catalogoId}/claves`)
    return { catalogoId, claves: r.data.data }
  } catch (e) {
    return rejectWithValue(e.response?.data?.message || 'Error al cargar claves')
  }
})

export const reordenarCategorias = createAsyncThunk('claves/reordenarCategorias', async ({ catalogoId, orden }, { rejectWithValue }) => {
  try {
    const r = await api.put(`/claves/catalogos/${catalogoId}/reordenar-categorias`, { orden })
    return { catalogoId, claves: r.data.data }
  } catch (e) {
    return rejectWithValue(e.response?.data?.message || 'Error al reordenar categorías')
  }
})

export const createClaveItem = createAsyncThunk('claves/createClaveItem', async (data, { rejectWithValue }) => {
  try { return (await api.post('/claves/claves', data)).data.data }
  catch (e) { return rejectWithValue(e.response?.data?.message || 'Error al crear clave') }
})

export const updateClaveItem = createAsyncThunk('claves/updateClaveItem', async ({ id, data }, { rejectWithValue }) => {
  try { return (await api.put(`/claves/claves/${id}`, data)).data.data }
  catch (e) { return rejectWithValue(e.response?.data?.message || 'Error al actualizar clave') }
})

export const deleteClaveItem = createAsyncThunk('claves/deleteClaveItem', async (id, { rejectWithValue }) => {
  try { await api.delete(`/claves/claves/${id}`); return id }
  catch (e) { return rejectWithValue(e.response?.data?.message || 'Error al eliminar clave') }
})

const initialState = {
  catalogos: [],
  claves: [],
  clavesCatalogoId: null, // de qué catálogo son las claves cargadas
  loadingCatalogos: false,
  loadingClaves: false,
  saving: false,
  error: null,
}

const slice = createSlice({
  name: 'claves',
  initialState,
  reducers: {
    clearError(state) { state.error = null },
  },
  extraReducers: (builder) => {
    builder
      // pending limpia error (patrón de emergenciasSlice/operadoresSlice): sin esto, un fallo
      // transitorio dejaba el Alert rojo de ClavesPage pegado toda la sesión, incluso tras
      // recargas exitosas (el estado Redux persiste entre navegaciones y nadie lo limpiaba).
      .addCase(fetchCatalogos.pending, (state) => { state.loadingCatalogos = true; state.error = null })
      .addCase(fetchCatalogos.fulfilled, (state, action) => {
        state.loadingCatalogos = false
        state.catalogos = action.payload
      })
      .addCase(fetchCatalogos.rejected, (state, action) => {
        state.loadingCatalogos = false
        state.error = action.payload || 'Error al cargar catálogos'
      })
      .addCase(createCatalogo.pending, (state) => { state.saving = true })
      .addCase(createCatalogo.fulfilled, (state, action) => {
        state.saving = false
        state.catalogos.push(action.payload)
      })
      .addCase(createCatalogo.rejected, (state, action) => {
        state.saving = false
        state.error = action.payload || 'Error al crear catálogo'
      })
      .addCase(updateCatalogo.fulfilled, (state, action) => {
        const i = state.catalogos.findIndex((c) => c.id === action.payload.id)
        if (i >= 0) state.catalogos[i] = { ...state.catalogos[i], ...action.payload }
      })
      .addCase(activarCatalogo.fulfilled, (state, action) => {
        state.catalogos = action.payload
      })
      .addCase(deleteCatalogo.fulfilled, (state, action) => {
        state.catalogos = state.catalogos.filter((c) => c.id !== action.payload)
        if (state.clavesCatalogoId === action.payload) {
          state.claves = []
          state.clavesCatalogoId = null
        }
      })
      .addCase(deleteCatalogo.rejected, (state, action) => {
        state.error = action.payload || 'Error al eliminar catálogo'
      })
      // claves
      .addCase(fetchClavesDeCatalogo.pending, (state) => { state.loadingClaves = true; state.error = null })
      .addCase(fetchClavesDeCatalogo.fulfilled, (state, action) => {
        state.loadingClaves = false
        state.claves = action.payload.claves
        state.clavesCatalogoId = action.payload.catalogoId
      })
      .addCase(fetchClavesDeCatalogo.rejected, (state, action) => {
        state.loadingClaves = false
        state.error = action.payload || 'Error al cargar claves'
      })
      .addCase(reordenarCategorias.fulfilled, (state, action) => {
        if (action.payload.catalogoId === state.clavesCatalogoId) state.claves = action.payload.claves
      })
      .addCase(reordenarCategorias.rejected, (state, action) => {
        state.error = action.payload || 'Error al reordenar categorías'
      })
      .addCase(createClaveItem.pending, (state) => { state.saving = true })
      .addCase(createClaveItem.fulfilled, (state, action) => {
        state.saving = false
        if (action.payload.catalogoId === state.clavesCatalogoId) state.claves.push(action.payload)
        // actualizar conteo del catálogo
        const cat = state.catalogos.find((c) => c.id === action.payload.catalogoId)
        if (cat && typeof cat.clavesCount === 'number') cat.clavesCount += 1
      })
      .addCase(createClaveItem.rejected, (state, action) => {
        state.saving = false
        state.error = action.payload || 'Error al crear clave'
      })
      .addCase(updateClaveItem.fulfilled, (state, action) => {
        const i = state.claves.findIndex((c) => c.id === action.payload.id)
        if (i >= 0) state.claves[i] = action.payload
      })
      .addCase(deleteClaveItem.fulfilled, (state, action) => {
        const removed = state.claves.find((c) => c.id === action.payload)
        state.claves = state.claves.filter((c) => c.id !== action.payload)
        if (removed) {
          const cat = state.catalogos.find((c) => c.id === removed.catalogoId)
          if (cat && typeof cat.clavesCount === 'number') cat.clavesCount = Math.max(0, cat.clavesCount - 1)
        }
      })
  },
})

export const { clearError } = slice.actions
export default slice.reducer
